/**
 * TMDB API helpers from Netflix clone (lib/tmdb.ts).
 * Details, cast, similar, TV seasons/episodes, getPosterUrl. Used only by the movies feature.
 */

const API_KEY = process.env.EXPO_PUBLIC_TMDB_API_KEY || ''
const BASE = 'https://api.themoviedb.org/3'

function getUrl(path: string, params: Record<string, string> = {}) {
  const q = new URLSearchParams({
    ...(process.env.EXPO_PUBLIC_TMDB_ACCESS_TOKEN ? {} : {api_key: API_KEY}),
    language: 'en-US',
    ...params,
  })
  return `${BASE}${path}?${q}`
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {}
  if (process.env.EXPO_PUBLIC_TMDB_ACCESS_TOKEN) {
    headers['Authorization'] = `Bearer ${process.env.EXPO_PUBLIC_TMDB_ACCESS_TOKEN}`
  }
  return headers
}

async function fetchUrl(url: string, signal?: AbortSignal): Promise<Response> {
  return fetch(url, {signal, headers: getHeaders()})
}

export interface Genre {
  id: number
  name: string
}

export interface CastMember {
  id: number
  name: string
  character?: string
  profile_path: string | null
}

export interface TVSeason {
  air_date: string | null
  episode_count: number
  id: number
  name: string
  season_number: number
  poster_path: string | null
}

export interface TVEpisode {
  id: number
  name: string
  episode_number: number
  season_number: number
  still_path: string | null
  runtime: number | null
  overview?: string
}

export interface MovieDetails {
  id: number
  title?: string
  name?: string
  overview: string
  poster_path: string | null
  backdrop_path: string | null
  release_date?: string
  first_air_date?: string
  vote_average: number
  runtime?: number
  genres: Genre[]
  credits?: {cast: CastMember[]}
  videos?: {results: {key: string; type: string}[]}
}

export interface SimilarResult {
  id: number
  title?: string
  name?: string
  poster_path: string | null
  backdrop_path: string | null
  release_date?: string
  first_air_date?: string
  vote_average: number
  media_type?: 'movie' | 'tv'
}

export async function getDetails(
  type: 'movie' | 'tv',
  tmdbId: number,
  signal?: AbortSignal,
): Promise<MovieDetails | null> {
  const path = type === 'movie' ? `/movie/${tmdbId}` : `/tv/${tmdbId}`
  const url = getUrl(path, {append_to_response: 'credits,videos'})
  const res = await fetchUrl(url, signal)
  if (!res.ok) return null
  return (await res.json()) as MovieDetails
}

export async function getSimilar(
  type: 'movie' | 'tv',
  tmdbId: number,
  signal?: AbortSignal,
): Promise<SimilarResult[]> {
  const path =
    type === 'movie' ? `/movie/${tmdbId}/similar` : `/tv/${tmdbId}/similar`
  const res = await fetchUrl(getUrl(path), signal)
  if (!res.ok) return []
  const data = (await res.json()) as {results?: SimilarResult[]}
  return (data?.results ?? []).slice(0, 12)
}

export async function getTvSeasons(
  tmdbId: number,
  signal?: AbortSignal,
): Promise<TVSeason[]> {
  const res = await fetchUrl(getUrl(`/tv/${tmdbId}`), signal)
  if (!res.ok) return []
  const data = (await res.json()) as {seasons?: TVSeason[]}
  return data?.seasons ?? []
}

export async function getSeasonEpisodes(
  tmdbId: number,
  seasonNumber: number,
  signal?: AbortSignal,
): Promise<TVEpisode[]> {
  const res = await fetchUrl(
    getUrl(`/tv/${tmdbId}/season/${seasonNumber}`),
    signal,
  )
  if (!res.ok) return []
  const data = (await res.json()) as {episodes?: TVEpisode[]}
  return data?.episodes ?? []
}

export function getPosterUrl(path: string | null, size = 'w342'): string {
  if (!path) return ''
  return `https://image.tmdb.org/t/p/${size}${path}`
}
